// src/components/laboratory/tabs/LaboratoryStatsTab.jsx
import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  Tag,
  Package,
  Users,
  FileText,
  TrendingUp,
  CheckCircle,
  Clock,
  XCircle,
  DollarSign,
  Percent,
  QrCode,
  Megaphone,
  BarChart3,
  AlertCircle,
  ScanLine
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { GET_LAB_SCAN_STATS } from '../../../api/laboratory-api';

const LaboratoryStatsTab = ({
  laboratory,
  promotions = [],
  products = [],
  users = [],
  contracts = [],
  adds = []
}) => {
  const now = new Date();

  const { data: scanStatsData, loading: scanStatsLoading } = useQuery(GET_LAB_SCAN_STATS, {
    variables: { laboratoryId: laboratory?.id },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network'
  });

  const scanStats = scanStatsData?.labScanStats || { totalScanned: 0, topProducts: [] };
  const topScannedProducts = scanStats.topProducts || [];
  const maxScanCount = topScannedProducts.length > 0 ? Math.max(...topScannedProducts.map(p => p.scanCount), 1) : 1;

  // ── Promotion KPIs ──────────────────────────────────────────────────────────
  const promoStats = useMemo(() => {
    const active   = promotions.filter(p => p.status === 'IN_PROGRESS' && new Date(p.endDate) >= now);
    const draft    = promotions.filter(p => p.status === 'DRAFT');
    const closed   = promotions.filter(p => p.status === 'CLOSED' || new Date(p.endDate) < now);
    const totalQR  = promotions.reduce((s, p) => s + (p.quantity || 0), 0);
    const totalVendeurCommission = promotions.reduce((s, p) => s + (p.commissionAmount || 0), 0);
    return { total: promotions.length, active: active.length, draft: draft.length, closed: closed.length, totalQR, totalVendeurCommission };
  }, [promotions, now]);

  // ── Contract KPIs ────────────────────────────────────────────────────────────
  const contractStats = useMemo(() => {
    const active  = contracts.filter(c => new Date(c.dateEnd) >= now);
    const expired = contracts.filter(c => new Date(c.dateEnd) < now);
    // Days until next expiry
    const nextExpiry = contracts
      .filter(c => new Date(c.dateEnd) >= now)
      .sort((a, b) => new Date(a.dateEnd) - new Date(b.dateEnd))[0];
    return { total: contracts.length, active: active.length, expired: expired.length, nextExpiry };
  }, [contracts, now]);

  // ── Status distribution (promotions) ────────────────────────────────────────
  const statusDist = useMemo(() => {
    const map = { IN_PROGRESS: 0, DRAFT: 0, CLOSED: 0 };
    promotions.forEach(p => { if (map[p.status] !== undefined) map[p.status]++; });
    const total = promotions.length || 1;
    return [
      { label: 'En cours',   count: map.IN_PROGRESS, pct: Math.round(map.IN_PROGRESS / total * 100), color: 'bg-green-500'  },
      { label: 'Brouillon',  count: map.DRAFT,        pct: Math.round(map.DRAFT        / total * 100), color: 'bg-yellow-400' },
      { label: 'Terminée',   count: map.CLOSED,       pct: Math.round(map.CLOSED       / total * 100), color: 'bg-red-400'   },
    ];
  }, [promotions]);

  // ── Top products (by number of promotions they appear in) ────────────────────
  const topProducts = useMemo(() => {
    const map = {};
    promotions.forEach(p => {
      (p.products || []).forEach(prod => {
        if (!map[prod.id]) map[prod.id] = { ...prod, promoCount: 0 };
        map[prod.id].promoCount++;
      });
    });
    return Object.values(map)
      .sort((a, b) => b.promoCount - a.promoCount)
      .slice(0, 5);
  }, [promotions]);

  // ── Commission distribution ──────────────────────────────────────────────────
  const commissionDist = useMemo(() => {
    const buckets = [
      { label: '0–5 DH',   min: 0,   max: 5,   count: 0 },
      { label: '5–10 DH',  min: 5,   max: 10,  count: 0 },
      { label: '10–20 DH', min: 10,  max: 20,  count: 0 },
      { label: '20+ DH',   min: 20,  max: Infinity, count: 0 },
    ];
    promotions.forEach(p => {
      const bucket = buckets.find(b => (p.commissionAmount || 0) >= b.min && (p.commissionAmount || 0) < b.max);
      if (bucket) bucket.count++;
    });
    const max = Math.max(...buckets.map(b => b.count), 1);
    return buckets.map(b => ({ ...b, pct: Math.round(b.count / max * 100) }));
  }, [promotions]);

  // ── Upcoming expirations ─────────────────────────────────────────────────────
  const upcomingExpirations = useMemo(() => {
    const in30 = new Date(now);
    in30.setDate(in30.getDate() + 30);
    return promotions
      .filter(p => {
        const end = new Date(p.endDate);
        return end >= now && end <= in30;
      })
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate))
      .slice(0, 5);
  }, [promotions, now]);

  // ── KPI card helper ──────────────────────────────────────────────────────────
  const KpiCard = ({ icon: Icon, label, value, sub, colorClass }) => (
    <div className={`rounded-lg border p-4 flex items-start space-x-3 ${colorClass}`}>
      <Icon className="h-8 w-8 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-medium opacity-75">{label}</p>
        <p className="text-2xl font-bold leading-tight">{value}</p>
        {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">

      {/* ── Section 1: Vue d'ensemble ─────────────────────────────────────── */}
      <section>
        <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <BarChart3 size={18} className="text-indigo-500" />
          Vue d'ensemble
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            icon={Tag}
            label="Promotions actives"
            value={promoStats.active}
            sub={`${promoStats.total} au total`}
            colorClass="bg-green-50 border-green-200 text-green-800"
          />
          <KpiCard
            icon={Package}
            label="Produits"
            value={products.length}
            sub="référencés"
            colorClass="bg-indigo-50 border-indigo-200 text-indigo-800"
          />
          <KpiCard
            icon={Users}
            label="Utilisateurs"
            value={users.length}
            sub="associés"
            colorClass="bg-blue-50 border-blue-200 text-blue-800"
          />
          <KpiCard
            icon={FileText}
            label="Contrats actifs"
            value={contractStats.active}
            sub={`${contractStats.expired} expirés`}
            colorClass="bg-purple-50 border-purple-200 text-purple-800"
          />
          <KpiCard
            icon={ScanLine}
            label="QR codes scannés"
            value={scanStatsLoading ? '...' : scanStats.totalScanned.toLocaleString()}
            sub="sur toutes les promotions"
            colorClass="bg-orange-50 border-orange-200 text-orange-800"
          />
        </div>
      </section>

      {/* ── Section 2: Promotions ──────────────────────────────────────────── */}
      <section>
        <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Tag size={18} className="text-green-500" />
          Promotions
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Status distribution */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-600 mb-4">Répartition par statut</h4>
            {promotions.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Aucune promotion</p>
            ) : (
              <div className="space-y-3">
                {statusDist.map(({ label, count, pct, color }) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-20 flex-shrink-0">{label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-3">
                      <div className={`${color} h-3 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-6 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Commission distribution */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-600 mb-4">Distribution des commissions vendeur</h4>
            {promotions.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Aucune promotion</p>
            ) : (
              <div className="space-y-3">
                {commissionDist.map(({ label, count, pct }) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-20 flex-shrink-0">{label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-3">
                      <div className="bg-green-500 h-3 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-6 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* QR + commission summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <KpiCard
            icon={QrCode}
            label="Codes QR totaux"
            value={promoStats.totalQR.toLocaleString()}
            sub="générés sur toutes les promos"
            colorClass="bg-blue-50 border-blue-200 text-blue-800"
          />
          <KpiCard
            icon={Clock}
            label="En attente (brouillon)"
            value={promoStats.draft}
            colorClass="bg-yellow-50 border-yellow-200 text-yellow-800"
          />
          <KpiCard
            icon={XCircle}
            label="Terminées / expirées"
            value={promoStats.closed}
            colorClass="bg-red-50 border-red-200 text-red-800"
          />
          <KpiCard
            icon={DollarSign}
            label="Commission vendeur moy."
            value={promotions.length > 0
              ? `${(promoStats.totalVendeurCommission / promotions.length).toFixed(2)} DH`
              : '—'}
            sub="par promotion"
            colorClass="bg-emerald-50 border-emerald-200 text-emerald-800"
          />
        </div>
      </section>

      {/* ── Section 3: Top produits ────────────────────────────────────────── */}
      <section>
        <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Package size={18} className="text-indigo-500" />
          Produits les plus promus
        </h3>
        {topProducts.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">
            <Package size={36} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun produit associé à des promotions</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Promotions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {topProducts.map((prod, i) => {
                  const pct = promotions.length > 0
                    ? Math.round(prod.promoCount / promotions.length * 100)
                    : 0;
                  return (
                    <tr key={prod.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-bold text-gray-400">{i + 1}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{prod.name}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {prod.price != null ? `${prod.price.toFixed(2)} DH` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-indigo-600">
                        {prod.promoCount}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-100 rounded-full h-2">
                            <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-400">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Section 4: Produits les plus scannés ──────────────────────────── */}
      <section>
        <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <ScanLine size={18} className="text-orange-500" />
          Produits les plus scannés
        </h3>
        {scanStatsLoading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500"></div>
          </div>
        ) : topScannedProducts.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">
            <ScanLine size={36} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun scan enregistré pour ce laboratoire</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="space-y-3">
              {topScannedProducts.map((prod, i) => {
                const pct = Math.round((prod.scanCount / maxScanCount) * 100);
                return (
                  <div key={prod.productId} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                    <span className="text-sm text-gray-700 w-40 truncate flex-shrink-0">{prod.productName}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                      <div
                        className="bg-orange-400 h-4 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-orange-700 w-14 text-right flex-shrink-0">
                      {prod.scanCount.toLocaleString()} scans
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-4 text-right">
              Total : {scanStats.totalScanned.toLocaleString()} scans
            </p>
          </div>
        )}
      </section>

      {/* ── Section 5: Expirations imminentes ─────────────────────────────── */}
      <section>
        <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <AlertCircle size={18} className="text-orange-500" />
          Promotions expirant dans 30 jours
        </h3>
        {upcomingExpirations.length === 0 ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">Aucune promotion n'expire dans les 30 prochains jours.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {upcomingExpirations.map(p => {
                const end = new Date(p.endDate);
                const daysLeft = Math.ceil((end - now) / 86400000);
                const urgency = daysLeft <= 3 ? 'text-red-600 bg-red-50' : daysLeft <= 7 ? 'text-orange-600 bg-orange-50' : 'text-yellow-700 bg-yellow-50';
                return (
                  <li key={p.id} className="px-4 py-3 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                      <p className="text-xs text-gray-500">
                        Fin: {format(end, 'dd MMMM yyyy', { locale: fr })}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${urgency}`}>
                      {daysLeft === 0 ? 'Dernier jour' : `${daysLeft}j restant${daysLeft > 1 ? 's' : ''}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      {/* ── Section 6: Contrats + Publicités ──────────────────────────────── */}
      <section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Contrats */}
          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <FileText size={18} className="text-purple-500" />
              Contrats
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard
                icon={CheckCircle}
                label="Contrats actifs"
                value={contractStats.active}
                colorClass="bg-purple-50 border-purple-200 text-purple-800"
              />
              <KpiCard
                icon={XCircle}
                label="Contrats expirés"
                value={contractStats.expired}
                colorClass="bg-red-50 border-red-200 text-red-800"
              />
            </div>
            {contractStats.nextExpiry && (
              <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-xs text-purple-600 font-medium">Prochain contrat expirant</p>
                <p className="text-sm font-semibold text-purple-800 mt-0.5">
                  {contractStats.nextExpiry.nom || contractStats.nextExpiry.number}
                </p>
                <p className="text-xs text-purple-500">
                  {format(new Date(contractStats.nextExpiry.dateEnd), 'dd MMMM yyyy', { locale: fr })}
                </p>
              </div>
            )}
          </div>

          {/* Publicités */}
          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Megaphone size={18} className="text-pink-500" />
              Publicités
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard
                icon={Megaphone}
                label="Total publicités"
                value={adds.length}
                colorClass="bg-pink-50 border-pink-200 text-pink-800"
              />
              <KpiCard
                icon={CheckCircle}
                label="En cours"
                value={adds.filter(a => {
                  const s = new Date(a.dateDebut), e = new Date(a.dateFin);
                  return now >= s && now <= e;
                }).length}
                colorClass="bg-rose-50 border-rose-200 text-rose-800"
              />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LaboratoryStatsTab;
